import { useCallback, useEffect, useState } from "react";
import { MdLocationOn } from "react-icons/md";
import { PiVideoFill } from "react-icons/pi";
import { useHistory, useParams } from "react-router-dom";
import InterviewModal from "../../components/Modal/InterviewModal";
import HrResults from "../../components/Rating/HrResult";
import axiosInstance from "../../reactWrapper/endpoints/axios";
import { useTalentCbtDetailsQuery } from "../../reactWrapper/endpoints/talent/talent.api";
import OverallRating from "../../components/Rating/OverallRating";
import Badge from "../../components/Badge/Badge";
import TalentAvatar from "../../components/Cards/TalentAvatar";
import DeepProfile from "../../components/Modal/DeepProfile";
import DeleteTalentButton from "../../components/Talent/DeleteTalentButton";
import { awaitingInvite } from "../../components/Talent/ResendInviteButton";

export type BadgeInfo = {
  title: string;
  experience: string;
};

const TalentDetails = () => {
  const { id } = useParams() as any;
  const history = useHistory();
  const { talentCbtDetails } = useTalentCbtDetailsQuery(id ?? "");
  let verified = false;
  const [languages, setLanguages] = useState<BadgeInfo[]>([]);
  const [skills, setSkills] = useState<BadgeInfo[]>([]);
  const [stacks, setStacks] = useState<BadgeInfo[]>([]);
  const [frameworks, setFrameworks] = useState<BadgeInfo[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const fetchLanguages = useCallback(async () => {
    if (!talentCbtDetails || !talentCbtDetails?.userCbtLanguage) return;

    const data: BadgeInfo[] = [];
    const languageInfo = talentCbtDetails!.userCbtLanguage as any;
    delete languageInfo.user_id;
    delete languageInfo.id;
    delete languageInfo.created_at;
    delete languageInfo.updated_at;
    const length = Math.ceil(Object.keys(languageInfo).length / 2);
    const fetches = [];

    try {
      for (let idx = 1; idx <= length; idx++) {
        const id = languageInfo["language" + idx];
        if (id !== null && id !== undefined) {
          fetches.push(
            await axiosInstance.get(`/all/getCbtParamsRealname/${id}/language`)
          );
        }
      }

      await Promise.all(fetches).then((responses) => {
        responses.forEach((response, idx) => {
          data.push({
            title: response.data.data,
            experience: languageInfo["experience" + (idx + 1)],
          });
        });
      });
    } catch (error) {
      console.log(error);
    }

    setLanguages(data);
    return data;
  }, [talentCbtDetails]);

  const fetchSkills = useCallback(async () => {
    if (!talentCbtDetails || !talentCbtDetails.userCbtSkill) return;

    const data: BadgeInfo[] = [];
    const skillInfo = talentCbtDetails!.userCbtSkill as any;
    delete skillInfo.user_id;
    delete skillInfo.id;
    delete skillInfo.created_at;
    delete skillInfo.updated_at;
    const length = Math.ceil(Object.keys(skillInfo).length / 2);
    const fetches = [];

    try {
      for (let idx = 1; idx <= length; idx++) {
        const id = skillInfo["skill" + idx];
        if (id !== null && id !== undefined) {
          fetches.push(
            await axiosInstance.get(`/all/getCbtParamsRealname/${id}/skill`)
          );
        }
      }

      await Promise.all(fetches).then((responses) => {
        responses.forEach((response, idx) => {
          data.push({
            title: response.data.data,
            experience: skillInfo["experience" + (idx + 1)],
          });
        });
      });
    } catch (error) {
      console.log(error);
    }

    setSkills(data);
    return data;
  }, [talentCbtDetails]);

  const fetchStacks = useCallback(async () => {
    if (!talentCbtDetails || !talentCbtDetails.userCbtStack) return;

    const data: BadgeInfo[] = [];
    const stackInfo = talentCbtDetails!.userCbtStack as any;
    delete stackInfo.user_id;
    delete stackInfo.id;
    delete stackInfo.created_at;
    delete stackInfo.updated_at;
    const length = Math.ceil(Object.keys(stackInfo).length / 2);
    const fetches = [];

    try {
      for (let idx = 1; idx <= 1; idx++) {
        const id = stackInfo["stack" + idx];
        if (id !== null && id !== undefined) {
          fetches.push(
            await axiosInstance.get(`/all/getCbtParamsRealname/${id}/stack`)
          );
        }
      }

      await Promise.all(fetches).then((responses) => {
        responses.forEach((response, idx) => {
          data.push({
            title: response.data.data,
            experience: stackInfo["experience" + (idx + 1)],
          });
        });
      });
    } catch (error) {
      console.log(error);
    }

    setStacks(data);
    return data;
  }, [talentCbtDetails]);

  const fetchFrameworks = useCallback(async () => {
    if (!talentCbtDetails || !talentCbtDetails.userCbtFramework) return;

    const data: BadgeInfo[] = [];
    const frameworkInfo = talentCbtDetails!.userCbtFramework as any;
    delete frameworkInfo.user_id;
    delete frameworkInfo.id;
    delete frameworkInfo.created_at;
    delete frameworkInfo.updated_at;
    const length = Math.ceil(Object.keys(frameworkInfo).length / 2);
    const fetches = [];

    try {
      for (let idx = 1; idx <= length; idx++) {
        const id = frameworkInfo["framework" + idx];
        if (id !== null && id !== undefined) {
          fetches.push(
            await axiosInstance.get(`/all/getCbtParamsRealname/${id}/framework`)
          );
        }
      }

      Promise.all(fetches).then((responses) => {
        responses.forEach((response, idx) => {
          data.push({
            title: response.data.data,
            experience: frameworkInfo["experience" + (idx + 1)],
          });
        });
      });
    } catch (error) {
      console.log(error);
    }

    setFrameworks(data);
    return data;
  }, [talentCbtDetails]);

  useEffect(() => {
    if (!talentCbtDetails) return;

    Promise.all([
      fetchLanguages(),
      fetchSkills(),
      fetchStacks(),
      fetchFrameworks(),
    ]).then(([languages, skills, stacks, frameworks]) => {
      // setLanguages(languages ?? []);
      // setSkills(skills ?? []);
      // setStacks(stacks ?? []);
      // setFrameworks(frameworks ?? []);
    });
  }, [talentCbtDetails]);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* IDENTITY + ABOUT */}
      <section className="rounded-2xl border bg-card p-5 shadow-ui-sm">
        <TalentAvatar
          size={14}
          verified={verified}
          data={talentCbtDetails}
          edit={false}
        />

        <div className="mt-6 border-t pt-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-orange">
            About
          </div>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {talentCbtDetails?.short_bio ?? "Nil"}
          </p>
        </div>
      </section>

      {/* PROFILE + RATING */}
      <div className="grid gap-6 lg:grid-cols-3">
        <section className="space-y-6 rounded-2xl border bg-card p-5 shadow-ui-sm lg:col-span-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-lg bg-surface px-3 py-2">
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Location
                <MdLocationOn className="h-3.5 w-3.5 text-brand-orange" />
              </div>
              <p className="mt-1 text-sm font-semibold text-brand-navy">
                {[
                  talentCbtDetails?.userLocation?.state,
                  talentCbtDetails?.userLocation?.country,
                  talentCbtDetails?.userLocation?.continent,
                ]
                  .filter(Boolean)
                  .join(", ") || "Nil"}
              </p>
            </div>

            <div className="rounded-lg bg-surface px-3 py-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Experience
              </div>
              <p className="mt-1 text-sm font-semibold capitalize text-brand-navy">
                {talentCbtDetails?.userCbtLevel?.level_name ?? "Nil"} Level
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-sm font-bold text-brand-navy">Role</h2>
            <div className="mt-2 flex flex-wrap gap-3">
              {stacks?.map(({ title, experience }, idx) => (
                <Badge
                  label={title?.replace(/-/g, " ")}
                  period={experience}
                  key={idx}
                />
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-sm font-bold text-brand-navy">Specialties</h2>
            <div className="mt-2 flex flex-wrap gap-3">
              {languages?.map(({ title, experience }, idx) => (
                <Badge
                  label={title?.replace(/-/g, " ")}
                  period={experience}
                  key={idx}
                />
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-sm font-bold text-brand-navy">Tools</h2>
            <div className="mt-2 flex flex-wrap gap-3">
              {frameworks?.map(({ title, experience }, idx) => (
                <Badge
                  label={title?.replace(/-/g, " ")}
                  period={experience}
                  key={idx}
                />
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-sm font-bold text-brand-navy">Skills</h2>
            <div className="mt-2 flex flex-wrap gap-3">
              {skills?.map(({ title, experience }, idx) => (
                <Badge
                  label={title?.replace(/-/g, " ")}
                  period={experience}
                  key={idx}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-2xl border bg-card p-5 shadow-ui-sm">
          <OverallRating talentId={talentCbtDetails?.id} />
        </section>
      </div>

      {/* ASSESSMENT */}
      <section className="rounded-2xl border bg-card p-5 shadow-ui-sm">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-orange">
          Assessment
        </div>
        <h2 className="font-display text-lg font-semibold text-brand-navy">CBT Test Score</h2>

        <div className="mt-4 grid gap-6 sm:grid-cols-2">
          <div className="space-y-4">
            <div className="tabular-nums text-2xl font-bold text-brand-navy sm:text-3xl">
              {talentCbtDetails?.userCbtResult.length > 0
                ? talentCbtDetails?.userCbtResult[0].total_score
                : 0}
              %
            </div>
            <DeepProfile userId={id ?? ""} />
          </div>

          {talentCbtDetails?.userCbtSoftSkill?.interview_link && talentCbtDetails?.userCbtSoftSkill?.interview_link !== 'undefined' && (
            <div className="flex flex-col items-start gap-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Interviews
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(true)}
                aria-label="Play interview recording"
                className="grid h-16 w-24 place-items-center rounded-xl border border-dashed bg-surface transition-colors hover:border-brand-orange"
              >
                <PiVideoFill className="h-8 w-8 text-brand-orange" />
              </button>
              <InterviewModal
                open={isOpen}
                close={() => setIsOpen(false)}
                url={talentCbtDetails?.userCbtSoftSkill?.interview_link}
              />
            </div>
          )}
        </div>
      </section>

      {/* HR RESULTS */}
      <HrResults userCbtSoftSkill={talentCbtDetails?.userCbtSoftSkill} />

      {/* Removing a talent an admin registered by mistake. It lives at the
          bottom of the profile rather than on the list: deleting from a row
          of a long table is too easy to do by accident, and here whoever is
          about to do it has just read who this person is.

          Renders nothing unless the account was admin-made and nobody has
          set a password on it - see awaitingInvite. */}
      {awaitingInvite(talentCbtDetails) ? (
        <section className="rounded-2xl border bg-card p-5 shadow-ui-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-foreground">
                Registered by an admin, not yet claimed
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                This account was created on their behalf and nobody has set a password on
                it yet, so it can still be removed. Once they set one, it cannot.
              </p>
            </div>
            <DeleteTalentButton
              talent={talentCbtDetails}
              onDeleted={() => history.push('/talents')}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default TalentDetails;

// import { UsersIcon } from '@heroicons/react/solid';
// import React from 'react';
// import { Language } from '../../components/Cbt/Language';
// import { Skills } from '../../components/Cbt/Skills';
// import { useGetACbtDetailQuery } from '../../reactWrapper/Redux/apiSlice';
// import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
// import { SoftSkill } from '../../components/Cbt/SoftSkill';

// const CbtResult = () => {
//   const {id} = useParams()
//   const {data, isLoading} = useGetACbtDetailQuery(id)
//   console.log(data)
//   const results = Array.isArray(data?.userCbtResult) ? data?.userCbtResult:[]
//   const [first] = results
//   const hrResult = data?.userCbtSoftSkill ? data?.userCbtSoftSkill:[]

//   if(isLoading){
//     return <div>Loading...</div>
//   }

//   return (
//    <div className='mt-0'>

//       <div className='grid grid-cols-6'>
//       <div className=" relative col-span-1 mt-0 relative bg-white rounded-lg shadow-md h-[200px] w-[200px] p-4">
//           <h2 className="text-l font-bold mb-2 text-gray-400">Total Score</h2>
//           <span className="absolute top-3 inline-block w-7 h-7 p-1 rounded-full bg-orange-200 right-3"><UsersIcon className="h-5 w-5 text-white" /></span>
//           <p className="text-center p-6 text-7xl font-bold text-black-400">{first?.total_score}</p>
//         </div>

//         <div className=" relative col-span-1 mt-0 ml-8 relative bg-white rounded-lg shadow-md h-[200px] w-[200px] p-4">
//           <h2 className="text-l font-bold mb-2 text-gray-400">Total Questions</h2>
//           <span className="absolute top-3 inline-block w-7 h-7 p-1 rounded-full bg-orange-200 right-3"><UsersIcon className="h-5 w-5 text-white" /></span>
//           <p className="text-center p-6 text-7xl font-bold text-black-400">{first?.total_number_of_question}</p>
//         </div>
//       </div>

//      <div className='grid grid-cols-3 gap-2'>
//        <Language lang = {first}/>

//        <Skills skill = {first}/>

//     </div>
//     <SoftSkill hr = {hrResult}/>

//   </div>
//   );
// };

// export default CbtResult;
